import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Avatar,
} from '@mui/material';
import {
  Assessment as AssessmentIcon,
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Restaurant as RestaurantIcon,
} from '@mui/icons-material';
import { reportsAPI, FinanceReportItem, MealReservationsReportItem, UnitServedMealsReportItem, MealRatingsReportItem, systemSettingsAPI, getImageUrl } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import * as jalaali from 'jalaali-js';
import * as XLSX from 'xlsx';
import PersianDatePicker from '../../components/PersianDatePicker';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const MonthlyReports: React.FC = () => {
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [excelCurrency, setExcelCurrency] = useState<'toman' | 'rial'>('toman');
  
  // Tab 0 State (Finance)
  const [financeData, setFinanceData] = useState<FinanceReportItem[]>([]);
  const now = new Date();
  const jNow = jalaali.toJalaali(now.getFullYear(), now.getMonth() + 1, now.getDate());
  const [year, setYear] = useState(jNow.jy);
  const [month, setMonth] = useState(jNow.jm);

  // Tab 1 & 2 & 3 State
  const [reservationsData, setReservationsData] = useState<MealReservationsReportItem[]>([]);
  const [servedMealsData, setServedMealsData] = useState<UnitServedMealsReportItem[]>([]);
  const [mealRatingsData, setMealRatingsData] = useState<MealRatingsReportItem[]>([]);
  
  const jStartG = jalaali.toGregorian(jNow.jy, jNow.jm, 1);
  const jStartGStr = `${jStartG.gy}-${String(jStartG.gm).padStart(2, '0')}-${String(jStartG.gd).padStart(2, '0')}`;
  const todayG = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  
  const [startDate, setStartDate] = useState(jStartGStr);
  const [endDate, setEndDate] = useState(todayG);

  const years = [1402, 1403, 1404, 1405];
  const months = [
    { value: 1, label: 'فروردین' }, { value: 2, label: 'اردیبهشت' }, { value: 3, label: 'خرداد' },
    { value: 4, label: 'تیر' }, { value: 5, label: 'مرداد' }, { value: 6, label: 'شهریور' },
    { value: 7, label: 'مهر' }, { value: 8, label: 'آبان' }, { value: 9, label: 'آذر' },
    { value: 10, label: 'دی' }, { value: 11, label: 'بهمن' }, { value: 12, label: 'اسفند' },
  ];

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    fetchData();
  }, [tabValue]);

  const fetchSettings = async () => {
    try {
      const settings = await systemSettingsAPI.getSystemSettings();
      if (settings && settings.excelCurrency) {
        setExcelCurrency(settings.excelCurrency as 'toman' | 'rial');
      }
    } catch (e) {
      console.error('Error fetching settings for excel currency', e);
    }
  };

  const setQuickDate = (type: 'pastWeek' | 'pastMonth' | 'nextWeek') => {
    const d = new Date();
    if (type === 'pastWeek') {
      d.setDate(d.getDate() - 7);
      setStartDate(d.toISOString().split('T')[0]);
      setEndDate(now.toISOString().split('T')[0]);
    } else if (type === 'pastMonth') {
      d.setMonth(d.getMonth() - 1);
      setStartDate(d.toISOString().split('T')[0]);
      setEndDate(now.toISOString().split('T')[0]);
    } else if (type === 'nextWeek') {
      d.setDate(d.getDate() + 7);
      setStartDate(now.toISOString().split('T')[0]);
      setEndDate(d.toISOString().split('T')[0]);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      if (tabValue === 0) {
        const startG = jalaali.toGregorian(year, month, 1);
        const daysInMonth = jalaali.jalaaliMonthLength(year, month);
        const endG = jalaali.toGregorian(year, month, daysInMonth);
        const sDate = `${startG.gy}-${String(startG.gm).padStart(2, '0')}-${String(startG.gd).padStart(2, '0')}`;
        const eDate = `${endG.gy}-${String(endG.gm).padStart(2, '0')}-${String(endG.gd).padStart(2, '0')}`;
        const result = await reportsAPI.getMonthlyFinanceReport(sDate, eDate);
        setFinanceData(result);
      } else if (tabValue === 1) {
        const result = await reportsAPI.getMealReservationsReport(startDate, endDate);
        setReservationsData(result);
      } else if (tabValue === 2) {
        const result = await reportsAPI.getUnitServedMealsReport(startDate, endDate);
        setServedMealsData(result);
      } else if (tabValue === 3) {
        const result = await reportsAPI.getMealRatingsReport(startDate, endDate);
        setMealRatingsData(result);
      }
    } catch (err) {
      console.error('Failed to fetch report', err);
    } finally {
      setLoading(false);
    }
  };

  const exportFinanceExcel = () => {
    const monthLabel = months.find(m => m.value === month)?.label;
    const fileName = `گزارش_مالی_${monthLabel}_${year}.xlsx`;
    const multiplier = excelCurrency === 'rial' ? 10 : 1;
    const currencyLabel = excelCurrency === 'rial' ? 'ریال' : 'تومان';
    
    const excelData = financeData.map((item, index) => ({
      'ردیف': index + 1,
      'نام و نام خانوادگی': `${item.firstname} ${item.lastname}`,
      'کدپرسنلی': item.personnelcode,
      'تعداد غذای آزاد': Number(item.free_food_count || 0),
      [`مبلغ غذای آزاد (${currencyLabel})`]: Number(item.free_food_amount || 0) * multiplier,
      'تعداد غذای سازمانی': Number(item.org_food_count || 0),
      [`سهم پرسنل (${currencyLabel})`]: Number(item.org_food_staff_amount || 0) * multiplier,
      [`سهم سازمان (${currencyLabel})`]: Number(item.org_total_amount || 0) * multiplier,
      [`قابل پرداخت پرسنل (${currencyLabel})`]: Number(item.staff_total_amount || 0) * multiplier,
    }));

    exportToExcelFile(excelData, fileName, 'گزارش مالی');
  };

  const exportReservationsExcel = () => {
    const fileName = `آمار_رزرو_غذاها.xlsx`;
    const multiplier = excelCurrency === 'rial' ? 10 : 1;
    const currencyLabel = excelCurrency === 'rial' ? 'ریال' : 'تومان';

    const excelData = reservationsData.map((item, index) => ({
      'ردیف': index + 1,
      'نام غذا': item.meal_title,
      'رزرو پرسنلی (شرکتی)': item.org_count,
      'رزرو مهمان (آزاد)': item.guest_count,
      'کل تعداد رزرو': item.total_reservations,
      [`مبلغ کل (${currencyLabel})`]: item.total_price * multiplier,
    }));
    exportToExcelFile(excelData, fileName, 'رزرو غذاها');
  };

  const exportServedMealsExcel = () => {
    const fileName = `آمار_غذاهای_سرو_شده.xlsx`;
    const multiplier = excelCurrency === 'rial' ? 10 : 1;
    const currencyLabel = excelCurrency === 'rial' ? 'ریال' : 'تومان';

    const excelData = servedMealsData.map((item, index) => ({
      'ردیف': index + 1,
      'واحد سازمانی': item.unit_title || 'بدون واحد',
      'نام غذا': item.meal_title,
      'سرو پرسنلی': item.org_served_count,
      'سرو مهمان': item.guest_served_count,
      'کل تعداد سرو شده': item.total_served,
      [`مبلغ کل (${currencyLabel})`]: item.total_price * multiplier,
      [`سهم پرسنل (${currencyLabel})`]: item.staff_total_amount * multiplier,
      [`سهم سازمان (${currencyLabel})`]: item.org_total_amount * multiplier,
    }));
    exportToExcelFile(excelData, fileName, 'غذاهای سرو شده');
  };

  const exportToExcelFile = (excelData: any[], fileName: string, sheetName: string) => {
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    XLSX.writeFile(wb, fileName);
  };

  return (
    <Box sx={{ fontFamily: 'Vazir, sans-serif' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssessmentIcon fontSize="large" /> سیستم گزارشات یکپارچه
        </Typography>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, val) => setTabValue(val)} variant="scrollable" scrollButtons="auto">
          <Tab label="گزارش مالی" />
          <Tab label="آمار رزرو غذاها" />
          <Tab label="آمار غذاهای سرو شده" />
          <Tab label="گزارش کیفیت و امتیاز غذاها" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Card sx={{ borderRadius: '16px', mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="flex-end">
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>سال</InputLabel>
                  <Select value={year} onChange={(e) => setYear(e.target.value as number)} label="سال" sx={{ borderRadius: '12px', height: '56px' }}>
                    {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>ماه</InputLabel>
                  <Select value={month} onChange={(e) => setMonth(e.target.value as number)} label="ماه" sx={{ borderRadius: '12px', height: '56px' }}>
                    {months.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Button fullWidth variant="contained" size="large" startIcon={<SearchIcon />} onClick={fetchData} sx={{ height: '56px', borderRadius: '12px' }}>
                  مشاهده گزارش
                </Button>
              </Grid>
              <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button variant="contained" color="success" size="large" startIcon={<DownloadIcon />} onClick={exportFinanceExcel} disabled={financeData.length === 0} sx={{ height: '56px', borderRadius: '12px', minWidth: '200px' }}>
                  خروجی اکسل
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}>
            <CircularProgress size={60} thickness={4} />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>ردیف</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>نام و نام خانوادگی</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>کدپرسنلی</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>تعداد غذای آزاد</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>مبلغ غذای آزاد</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>تعداد غذای سازمانی</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>سهم پرسنل</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>سهم سازمان</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center', bgcolor: 'primary.dark' }}>قابل پرداخت پرسنل</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {financeData.map((item, index) => (
                  <TableRow key={item.user_id} hover>
                    <TableCell sx={{ textAlign: 'center' }}>{index + 1}</TableCell>
                    <TableCell 
                      sx={{ 
                        color: 'primary.main', 
                        cursor: 'pointer', 
                        fontWeight: 600,
                        '&:hover': { textDecoration: 'underline' } 
                      }}
                      onClick={() => navigate(`/admin/users/${item.user_id}/reports`)}
                      title="مشاهده گزارش و تمامی رزروهای این پرسنل"
                    >
                      {`${item.firstname} ${item.lastname}`}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{item.personnelcode}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{(Number(item.free_food_count) || 0).toLocaleString('fa-IR')}</TableCell>
                    <TableCell sx={{ textAlign: 'center', color: 'error.main' }}>{Number(item.free_food_amount || 0).toLocaleString('fa-IR')}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{(Number(item.org_food_count) || 0).toLocaleString('fa-IR')}</TableCell>
                    <TableCell sx={{ textAlign: 'center', color: 'success.main' }}>{Number(item.org_food_staff_amount || 0).toLocaleString('fa-IR')}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{Number(item.org_total_amount || 0).toLocaleString('fa-IR')}</TableCell>
                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', bgcolor: 'rgba(25, 118, 210, 0.04)' }}>
                      {Number(item.staff_total_amount || 0).toLocaleString('fa-IR')}
                    </TableCell>
                  </TableRow>
                ))}
                {financeData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} sx={{ textAlign: 'center', py: 10, opacity: 0.5 }}>دیتایی برای این بازه زمانی یافت نشد.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Card sx={{ borderRadius: '16px', mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="flex-end">
              <Grid size={{ xs: 12, md: 3 }}>
                <PersianDatePicker label="از تاریخ" value={startDate} onChange={setStartDate} />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <PersianDatePicker label="تا تاریخ" value={endDate} onChange={setEndDate} />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button fullWidth variant="outlined" onClick={() => setQuickDate('pastWeek')} sx={{ height: '56px', borderRadius: '12px' }}>۱ هفته گذشته</Button>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button fullWidth variant="outlined" onClick={() => setQuickDate('nextWeek')} sx={{ height: '56px', borderRadius: '12px' }}>۱ هفته آینده</Button>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button fullWidth variant="contained" size="large" startIcon={<SearchIcon />} onClick={fetchData} sx={{ height: '56px', borderRadius: '12px' }}>گزارش</Button>
              </Grid>
              <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button variant="contained" color="success" size="large" startIcon={<DownloadIcon />} onClick={exportReservationsExcel} disabled={reservationsData.length === 0} sx={{ height: '56px', borderRadius: '12px', minWidth: '200px' }}>
                  خروجی اکسل
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}><CircularProgress size={60} thickness={4} /></Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>ردیف</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>نام غذا</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>رزرو پرسنلی</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>رزرو مهمان</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>مبلغ کل (تومان)</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center', bgcolor: 'primary.dark' }}>کل تعداد رزرو</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reservationsData.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ textAlign: 'center' }}>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar 
                          src={item.meal_image ? getImageUrl(item.meal_image) : undefined} 
                          variant="rounded" 
                          sx={{ width: 32, height: 32 }}
                        >
                          <RestaurantIcon fontSize="small" />
                        </Avatar>
                        {item.meal_title}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{item.org_count.toLocaleString('fa-IR')}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{item.guest_count.toLocaleString('fa-IR')}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{item.total_price.toLocaleString('fa-IR')}</TableCell>
                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', bgcolor: 'rgba(25, 118, 210, 0.04)' }}>{item.total_reservations.toLocaleString('fa-IR')}</TableCell>
                  </TableRow>
                ))}
                {reservationsData.length === 0 && (
                  <TableRow><TableCell colSpan={6} sx={{ textAlign: 'center', py: 10, opacity: 0.5 }}>دیتایی یافت نشد.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Card sx={{ borderRadius: '16px', mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="flex-end">
              <Grid size={{ xs: 12, md: 3 }}>
                <PersianDatePicker label="از تاریخ" value={startDate} onChange={setStartDate} maxDate={todayG} />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <PersianDatePicker label="تا تاریخ" value={endDate} onChange={setEndDate} maxDate={todayG} />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button fullWidth variant="outlined" onClick={() => setQuickDate('pastWeek')} sx={{ height: '56px', borderRadius: '12px' }}>۱ هفته گذشته</Button>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button fullWidth variant="outlined" onClick={() => setQuickDate('pastMonth')} sx={{ height: '56px', borderRadius: '12px' }}>۱ ماه گذشته</Button>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button fullWidth variant="contained" size="large" startIcon={<SearchIcon />} onClick={fetchData} sx={{ height: '56px', borderRadius: '12px' }}>گزارش</Button>
              </Grid>
              <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button variant="contained" color="success" size="large" startIcon={<DownloadIcon />} onClick={exportServedMealsExcel} disabled={servedMealsData.length === 0} sx={{ height: '56px', borderRadius: '12px', minWidth: '200px' }}>
                  خروجی اکسل
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}><CircularProgress size={60} thickness={4} /></Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>ردیف</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>واحد سازمانی</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>نام غذا</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>سرو پرسنلی</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>سرو مهمان</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center' }}>مبلغ کل (تومان)</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center', bgcolor: 'primary.dark' }}>کل تعداد سرو شده</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {servedMealsData.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ textAlign: 'center' }}>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>{item.unit_title || 'بدون واحد'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar 
                          src={item.meal_image ? getImageUrl(item.meal_image) : undefined} 
                          variant="rounded" 
                          sx={{ width: 32, height: 32 }}
                        >
                          <RestaurantIcon fontSize="small" />
                        </Avatar>
                        {item.meal_title}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{item.org_served_count.toLocaleString('fa-IR')}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{item.guest_served_count.toLocaleString('fa-IR')}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>{item.total_price.toLocaleString('fa-IR')}</TableCell>
                    <TableCell sx={{ textAlign: 'center', fontWeight: 'bold', bgcolor: 'rgba(25, 118, 210, 0.04)' }}>{item.total_served.toLocaleString('fa-IR')}</TableCell>
                  </TableRow>
                ))}
                {servedMealsData.length === 0 && (
                  <TableRow><TableCell colSpan={7} sx={{ textAlign: 'center', py: 10, opacity: 0.5 }}>دیتایی یافت نشد.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Card sx={{ borderRadius: '16px', mb: 4, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3} alignItems="flex-end">
              <Grid size={{ xs: 12, md: 3 }}>
                <PersianDatePicker label="از تاریخ" value={startDate} onChange={setStartDate} maxDate={todayG} />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <PersianDatePicker label="تا تاریخ" value={endDate} onChange={setEndDate} maxDate={todayG} />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button fullWidth variant="outlined" onClick={() => setQuickDate('pastWeek')} sx={{ height: '56px', borderRadius: '12px', fontFamily: 'Vazir' }}>۱ هفته گذشته</Button>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button fullWidth variant="outlined" onClick={() => setQuickDate('pastMonth')} sx={{ height: '56px', borderRadius: '12px', fontFamily: 'Vazir' }}>۱ ماه گذشته</Button>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button fullWidth variant="contained" size="large" startIcon={<SearchIcon />} onClick={fetchData} sx={{ height: '56px', borderRadius: '12px', fontFamily: 'Vazir' }}>گزارش</Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}><CircularProgress size={60} thickness={4} /></Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: '16px', overflow: 'hidden' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center', fontFamily: 'Vazir' }}>ردیف</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', fontFamily: 'Vazir' }}>نام غذا</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center', fontFamily: 'Vazir' }}>میانگین امتیاز</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 'bold', textAlign: 'center', fontFamily: 'Vazir' }}>تعداد آرا</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mealRatingsData.map((item, index) => (
                  <TableRow key={index} hover>
                    <TableCell sx={{ textAlign: 'center', fontFamily: 'Vazir' }}>{(index + 1).toLocaleString('fa-IR')}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar 
                          src={item.meal_image ? getImageUrl(item.meal_image) : undefined} 
                          variant="rounded" 
                          sx={{ width: 32, height: 32 }}
                        >
                          <RestaurantIcon fontSize="small" />
                        </Avatar>
                        <span style={{ fontFamily: 'Vazir' }}>{item.meal_title}</span>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', fontFamily: 'Vazir' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 'bold', color: item.average_rating >= 4 ? 'success.main' : item.average_rating >= 3 ? 'warning.main' : 'error.main' }}>
                          {item.average_rating.toLocaleString('fa-IR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}
                        </Typography>
                        از ۵
                      </Box>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center', fontFamily: 'Vazir' }}>{item.total_ratings.toLocaleString('fa-IR')}</TableCell>
                  </TableRow>
                ))}
                {mealRatingsData.length === 0 && (
                  <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 10, opacity: 0.5, fontFamily: 'Vazir' }}>دیتایی یافت نشد.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>
    </Box>
  );
};

export default MonthlyReports;
